/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import moment from 'moment';

export default class AttendanceRoute extends Route {
  // Services
  @service
  dates;

  @service
  session;

  @service
  store;

  @service
  terms;

  @alias('session.school.jurisdiction')
  jurisdiction;

  model() {
    let group = this.modelFor('group');
    let dateFrom = this.dates.rolloverDate;
    let schoolId = this.session.activeSchool.id;

    let data = {
      group,
      learners: this.modelFor('group.insights').learners,
      records: this.store.query('attendance/record', {
        groupId: group.id,
        dateFrom,
      }),
      terms: this.store.findAll('term'),
      timetable: this.store.query('timetable/structure', {
        schoolId,
        include: 'periods',
      }),
      staff: this.store.query('person', { type: PersonType.STAFF }),
      attendancePage: this.store
        .query('posts/page', { isAttendance: true })
        .then((pages) => pages[0]),
    };

    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      data.dayCodes = this.store.query('attendance/nz-moe-day', {
        dateFrom,
        groupId: group.id,
      });
    }
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      data.responseActivities = this.store.query(
        'attendance/response-activity',
        {
          groupIds: JSON.stringify([group.id]),
          schoolId,
        },
      );
      data.responseActivityConfig = this.store.query(
        'attendance/response-activity-configuration',
        { schoolId },
      );
      data.termlyAbsences = data.terms.then((terms) => {
        let currentAcademicYear = this.dates.currentAcademicYear;
        let currentYearTerms = terms.filter((term) => {
          if (!term.start || !term.end) {
            return false;
          }

          return term.academicYear === currentAcademicYear.toString();
        });

        if (currentYearTerms.length === 0) {
          return;
        }

        return Promise.all(
          currentYearTerms.map((term) =>
            this.store
              .query('attendance/day-code', {
                dateFrom: term.start,
                dateTo: term.end,
                analysis: true,
                groupIds: JSON.stringify([group.id]),
              })
              .then((dayCodes) => ({
                term: term.term,
                dayCodes: dayCodes || [],
              })),
          ),
        ).then((results) => results);
      });
    }

    return hash(data);
  }

  resetController(controller) {
    // Reset filters and selections on route exit
    controller.filters = [];
    controller.selectedAmp = [];
    // Unload all day codes from the store
    this.store.unloadAll('attendance/day-code');
  }

  async afterModel(model) {
    super.afterModel(...arguments);

    let group = this.modelFor('group');

    let currentTerm = this.get('terms.currentTerm');

    // query gives a plain Promise. Wait for the records, because the
    // controller reads the arrays.
    let thresholds = { current: [], previous: [] };
    if (!isEmpty(currentTerm)) {
      thresholds.current = this.store.query('attendance/threshold', {
        groupIds: JSON.stringify([group.id]),
        dateFrom: currentTerm.start,
        dateTo: moment().format('YYYY-MM-DD'),
      });
    }

    if (!isEmpty(currentTerm) && !isEmpty(currentTerm.previous)) {
      thresholds.previous = this.store.query('attendance/threshold', {
        groupIds: JSON.stringify([group.id]),
        dateFrom: currentTerm.previous.get('start'),
        dateTo: currentTerm.previous.get('end'),
      });
    }

    let { current, previous } = await hash(thresholds);
    model.currentTermThresholds = current;
    model.previousTermThresholds = previous;
  }
}
